import { contentTexture, contentTextureGeneration } from "../content_textures"
import { Bounds, NumberedTileMeta, TileSize } from "game-common/models"
import { Container, Rectangle, Renderer, Sprite, Texture } from "pixi.js"
import { getRendererApp } from "../renderer_app"
import { artAtlas } from "./render_texture_atlas"
import { TileMapMetaProvider } from "game-common/tile_map_meta_provider"

export class TileTextureExtractor {
    static instance: TileTextureExtractor = new TileTextureExtractor()

    static textureCache: Map<string, Texture> = new Map()
    static compositeTileSpriteBounds: Map<string, Bounds> = new Map()
    private static generation = 0

    extract = (tilemapMetaProvider: TileMapMetaProvider, tilesetId: string, compositeTileId: string): Texture => {
        if (TileTextureExtractor.generation !== contentTextureGeneration()) {
            TileTextureExtractor.generation = contentTextureGeneration()
            // Drop memoized references, not textures: visible sprites may still own the old art.
            TileTextureExtractor.textureCache.clear()
            TileTextureExtractor.compositeTileSpriteBounds.clear()
        }
        const parentTileSet = tilemapMetaProvider.getTilesetById(tilesetId)

        const compositeTileMeta = tilemapMetaProvider.resolveCompositeTileIdTileMeta(compositeTileId, tilesetId)
        if (!compositeTileMeta) {
            return null
        }

        const fullTileId = `${parentTileSet.tilesetId}-${compositeTileId}`
        let texture = TileTextureExtractor.textureCache.get(fullTileId)
        // Only reuse a cached texture that is still valid. A DESTROYED texture (orig === null, or a
        // destroyed baseTexture) can linger in this module-level static cache -- e.g. left behind
        // across an HMR reload by code that used to evict+destroy these -- and handing it to a Sprite
        // crashes Pixi in calculateVertices/containsPoint ("Cannot read properties of null (reading
        // 'width')"). If the cached entry is stale, fall through and regenerate it below.
        if (texture && texture.orig && !texture.baseTexture?.destroyed) {
            return texture
        }

        const { width } = parentTileSet
        const children: NumberedTileMeta[] = compositeTileMeta.children || []

        const bounds: Bounds = new Bounds(0, 0, 0, 0)
        const childBounds = children.map(childTileMeta => {
            const tileNumber = childTileMeta.tileNumber - 1
            const tileSetX = tileNumber % width
            const tileSetY = Math.floor(tileNumber / width)

            const childBounds: Bounds = new Bounds(tileSetX, tileSetY, tileSetX + 1, tileSetY + 1)
            return childBounds
        })

        bounds.widen(childBounds)
        const xx = bounds.x1
        const yy = bounds.y1

        bounds.translate(-bounds.x1, -bounds.y1)
        TileTextureExtractor.compositeTileSpriteBounds.set(fullTileId, bounds)

        const rect = new Rectangle(0, 0, bounds.width * TileSize, bounds.height * TileSize)

        const containingSprite = new Container()

        children.forEach(childTileMeta => {
            const tileId = `${childTileMeta.tileNumber}`
            const tileNumber = childTileMeta.tileNumber - 1
            const tileSetX = (tileNumber % width) - xx
            const tileSetY = Math.floor(tileNumber / width) - yy

            const subtileSprite = new Sprite(contentTexture(`${parentTileSet.tilesetId}-${tileId}`))
            subtileSprite.x = tileSetX * TileSize - 0
            subtileSprite.y = tileSetY * TileSize + 0
            containingSprite.addChild(subtileSprite)
        })

        // Drawn into a shared page (render_texture_atlas.ts) so a storey's composites batch together; one too big for a
        // page gets a texture of its own.
        const renderer = getRendererApp().renderer as Renderer
        texture = artAtlas.put(renderer, containingSprite, rect) ?? renderer.generateTexture(containingSprite, { region: rect })
        containingSprite.destroy({ children: true })
        TileTextureExtractor.textureCache.set(fullTileId, texture)

        return texture
    }
}
